// src/components/CameraInfoOverlay.jsx
import React, { useState, useEffect, useRef } from "react";
import "./styles.css";

export const CameraInfoOverlay = ({
  pathName,
  videoRef,
  mediaMtxDns = "http://192.168.0.117:9997",
}) => {
  const [visible, setVisible] = useState(false);
  const [info, setInfo] = useState({
    ready: false,
    since: "-",
    codec: "-",
    resolution: "–",
    optimalResolution: "–",
    frameRate: "–",
    bitrateIn: "–",
    bitrateOut: "–",
    viewers: 0,
    bytesReceived: 0,
    bytesSent: 0,
    dropped: 0,
    totalFrames: 0,
    buffer: "–",
    networkActivity: "–",
    connectionSpeed: "–",
    date: new Date().toLocaleString(),
  });

  // refs
  const lastBytesInRef = useRef(0);
  const lastBytesOutRef = useRef(0);
  const lastTimeRef = useRef(Date.now());
  const intervalRef = useRef(null);
  const rafRef = useRef(null);
  const isMountedRef = useRef(true); // Defensive guard for async state updates

  // FPS smoothing samples
  const fpsSamplesRef = useRef([]); // stores recent fps samples
  const lastFrameCountRef = useRef(0); // last total frames read

  // Helper: format bytes -> KB/MB
  const fmtBytes = (b) => {
    if (b == null) return "–";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Helper: derive optimal res from height (prefer player value)
  const optimalFromHeight = (height) => {
    if (!height || height <= 0) return null;
    if (height >= 2160) return "4K";
    if (height >= 1440) return "1440p";
    if (height >= 1080) return "1080p";
    if (height >= 720) return "720p";
    if (height >= 480) return "480p";
    return "360p";
  };

  // Fallback optimal resolution from bitrate (Mbps)
  const optimalFromBitrateMbps = (mbps) => {
    if (mbps == null || isNaN(mbps)) return "–";
    if (mbps >= 8) return "4K";
    if (mbps >= 4) return "1440p";
    if (mbps >= 2.5) return "1080p";
    if (mbps >= 1.0) return "720p";
    if (mbps >= 0.5) return "480p";
    return "360p";
  };

  // --- Fetch stats from MediaMTX every 2s ---
  const fetchListAndUpdate = async () => {
    if (!pathName) return;
    
    try {
      const res = await fetch(`${mediaMtxDns}/v3/paths/list`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      const p = data.items?.find((x) => x.name === pathName);
      const now = Date.now();

      // Defensive guard: don't update state if component unmounted
      if (!isMountedRef.current) return;

      if (!p) {
        setInfo((prev) => ({ ...prev, ready: false, viewers: 0 }));
        lastBytesInRef.current = 0;
        lastBytesOutRef.current = 0;
        lastTimeRef.current = now;
        return;
      }

      const bytesR = p.bytesReceived || 0;
      const bytesS = p.bytesSent || 0;
      const prevIn = lastBytesInRef.current;
      const prevOut = lastBytesOutRef.current;
      const dt = (now - lastTimeRef.current) / 1000 || 1;

      const inBps = prevIn ? ((bytesR - prevIn) * 8) / dt : 0;
      const outBps = prevOut ? ((bytesS - prevOut) * 8) / dt : 0;

      lastBytesInRef.current = bytesR;
      lastBytesOutRef.current = bytesS;
      lastTimeRef.current = now;

      // compute bitrates as strings
      const bitrateInStr = inBps
        ? `${(inBps / 1_000_000).toFixed(2)} Mbps`
        : "–";
      const bitrateOutStr = outBps
        ? `${(outBps / 1_000_000).toFixed(2)} Mbps`
        : "–";

      // determine network activity and connection speed
      const networkActivity =
        inBps < 500_000 ? "Low" : inBps < 2_000_000 ? "Medium" : "High";
      const connectionSpeedStr = inBps
        ? `${(inBps / 1000).toFixed(1)} Kbps`
        : "–";

      // If we don't yet have resolution from player, attempt to set optimalResolution from bitrate
      let optimalResolution = info.optimalResolution;
      if (!optimalResolution || optimalResolution === "–") {
        const mbps = inBps ? inBps / 1_000_000 : null;
        optimalResolution = optimalFromBitrateMbps(mbps);
      }

      setInfo((prev) => ({
        ...prev,
        ready: p.ready,
        since: p.readyTime
          ? new Date(p.readyTime).toLocaleString()
          : prev.since,
        codec: p.tracks?.join(", ") || prev.codec,
        bitrateIn: bitrateInStr,
        bitrateOut: bitrateOutStr,
        viewers: p.readers?.length || 0,
        bytesReceived: bytesR,
        bytesSent: bytesS,
        networkActivity,
        connectionSpeed: connectionSpeedStr,
        optimalResolution,
        date: new Date().toLocaleString(),
      }));
    } catch (err) {
      console.error("Failed to fetch /v3/paths/list:", err);
    }
  };

  // --- Player metrics (throttled to 3s, with negative-delta protection + smoothing) ---
  const updatePlayerStats = (() => {
    let lastUpdate = 0;
    return () => {
      const now = Date.now();
      if (now - lastUpdate < 3000) return; // every 3s
      lastUpdate = now;

      const v =
        (videoRef && videoRef.current) || document.querySelector("video");
      if (!v) return;

      // playback quality
      const quality =
        typeof v.getVideoPlaybackQuality === "function"
          ? v.getVideoPlaybackQuality()
          : null;
      const dropped = quality
        ? quality.droppedVideoFrames || 0
        : v.webkitDroppedFrameCount || 0 || 0;
      const total = quality
        ? quality.totalVideoFrames || 0
        : v.webkitDecodedFrameCount || 0 || 0;

      // if counter reset (total < last) then reset last and skip calculating fps this sample
      const lastTotal = lastFrameCountRef.current || 0;
      if (total < lastTotal) {
        // reset and don't add a negative sample
        lastFrameCountRef.current = total;
        // still update dropped / buffer / resolution but skip fps sample
        const buffer = v.buffered?.length
          ? (v.buffered.end(0) - v.currentTime).toFixed(2) + " s"
          : "–";
        const width = v.videoWidth;
        const height = v.videoHeight;
        const resStr = width && height ? `${width}×${height}` : info.resolution;

        // set optimal resolution based on real height if available
        const optRes = optimalFromHeight(height) || info.optimalResolution;

        setInfo((prev) => ({
          ...prev,
          resolution: resStr,
          optimalResolution: optRes,
          dropped,
          totalFrames: total,
          buffer,
        }));
        return;
      }

      const deltaFrames = total - lastTotal;
      lastFrameCountRef.current = total;

      // fps for the 3-second interval
      const fpsNow = deltaFrames > 0 ? deltaFrames / 3 : 0; // frames per sec
      if (fpsNow > 0) {
        fpsSamplesRef.current.push(fpsNow);
        if (fpsSamplesRef.current.length > 6) fpsSamplesRef.current.shift(); // keep last 6 samples
      }

      // compute smoothed average fps
      const samples = fpsSamplesRef.current;
      const avgFps = samples.length
        ? samples.reduce((a, b) => a + b, 0) / samples.length
        : 0;

      const buffer = v.buffered?.length
        ? (v.buffered.end(0) - v.currentTime).toFixed(2) + " s"
        : "–";
      const width = v.videoWidth;
      const height = v.videoHeight;
      const resStr = width && height ? `${width}×${height}` : "–";
      const optRes = optimalFromHeight(height) || info.optimalResolution;

      setInfo((prev) => ({
        ...prev,
        resolution: resStr,
        optimalResolution: optRes,
        frameRate: avgFps ? `${avgFps.toFixed(1)} fps` : "–",
        dropped,
        totalFrames: total,
        buffer,
      }));
    };
  })();

  // --- Start/stop loops depending on visibility ---
  useEffect(() => {
    // Set mounted flag on mount
    isMountedRef.current = true;
    
    if (!visible) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    (async () => {
      await fetchListAndUpdate();
      // Check if still mounted before setting interval
      if (!isMountedRef.current) return;
      intervalRef.current = setInterval(fetchListAndUpdate, 2000);

      const loop = () => {
        if (!isMountedRef.current) return;
        updatePlayerStats();
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    })();

    return () => {
      // Clear mounted flag on unmount
      isMountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, pathName, videoRef?.current]);

  return (
    <div className="info-overlay-wrapper" style={{zIndex: 99}}>
      <button
        className="info-toggle-btn"
        onClick={() => setVisible((v) => !v)}
        title={visible ? "Hide Info" : "Show Info"}
        aria-label={visible ? "Hide stream diagnostics" : "Show stream diagnostics"}
        aria-expanded={visible}
      >
        <span className="icon">ⓘ</span>
        <span className="arrow">{visible ? "▲" : "▼"}</span>
      </button>

      {visible && (
        <div className="info-overlay" role="region" aria-label="Stream diagnostics information">
          <div className="info-title">Stream Diagnostics</div>

          <div className="info-row">
            <span>Path:</span>
            <span>{pathName}</span>
          </div>
          <div className="info-row">
            <span>Status:</span>
            <span style={{ color: info.ready ? "limegreen" : "red" }}>
              {info.ready ? "Online" : "Offline"}
            </span>
          </div>

          <div className="info-row">
            <span>Resolution (view):</span>
            <span>{info.resolution}</span>
          </div>
          <div className="info-row">
            <span>Optimal Res:</span>
            <span>{info.optimalResolution}</span>
          </div>
          <div className="info-row">
            <span>Frame Rate:</span>
            <span>{info.frameRate}</span>
          </div>
          <div className="info-row">
            <span>Codec:</span>
            <span>{info.codec}</span>
          </div>

          <div className="info-row">
            <span>Bitrate (in):</span>
            <span>{info.bitrateIn}</span>
          </div>
          <div className="info-row">
            <span>Bitrate (out):</span>
            <span>{info.bitrateOut}</span>
          </div>

          <div className="info-row">
            <span>Bytes In / Out:</span>
            <span>
              {fmtBytes(info.bytesReceived)} / {fmtBytes(info.bytesSent)}
            </span>
          </div>

          <div className="info-row">
            <span>Viewers:</span>
            <span>{info.viewers}</span>
          </div>
          <div className="info-row">
            <span>Buffer:</span>
            <span>{info.buffer}</span>
          </div>
          <div className="info-row">
            <span>Dropped:</span>
            <span>
              {info.dropped} / {info.totalFrames}
            </span>
          </div>

          <div className="info-row">
            <span>Network Activity:</span>
            <span>{info.networkActivity}</span>
          </div>
          <div className="info-row">
            <span>Conn Speed:</span>
            <span>{info.connectionSpeed}</span>
          </div>

          <div className="info-row">
            <span>Started:</span>
            <span>{info.since}</span>
          </div>
          <div className="info-row">
            <span>Date:</span>
            <span>{info.date}</span>
          </div>
        </div>
      )}
    </div>
  );
};
